import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch } from '../../redux/store';
import {
	getProject,
	getProjectStatus,
	releaseProjectOnStore,
	submitProjectForReview,
} from '../../redux/reducers/projectsSlice';
import { Link, useHistory, useParams } from 'react-router-dom';
import {
	Button,
	Card,
	Col,
	Row,
	Modal,
	Spin,
	Timeline,
	Checkbox,
	Divider,
	message,
} from 'antd';
import OmniNotification from '../../components/OmniNotification';
import { useTranslation } from 'react-i18next';
import api, {
	CollaboratorPermission,
	ProductStatus,
	Project,
	ProjectStatusDto,
} from '../../api';
import { ReactComponent as UsersOutlined } from 'assets/img/icons/UsersOutlinedMedium.svg';
import linkIcon from 'assets/img/icons/IinkOpenNewWindow.svg';
import {
	AppstoreOutlined,
	BarcodeOutlined,
	CheckOutlined,
	CloseOutlined,
	DotChartOutlined,
	FileZipOutlined,
	RocketFilled,
	ShopFilled,
	SolutionOutlined,
	WalletOutlined,
	InfoCircleOutlined,
	CopyOutlined,
} from '@ant-design/icons';
import styles from './ProjectSummaryPage.module.less';
import cx from 'classnames';
import { userSelector } from '../../redux/selectors/userSelector';
import { isNil } from 'lodash';
import UpdateProjectNameModal from './UpdateProjectNameModal';
import { mapErrorToErrorMessage } from 'utils/errorUtils';
import copy from 'copy-to-clipboard';

type ParamsProps = {
	projectId: string;
};

export default function ProjectDetailsPage() {
	const { t } = useTranslation();
	const dispatch: AppDispatch = useDispatch();
	const projectId = parseInt(useParams<ParamsProps>().projectId);
	const [isLoading, setIsLoading] = useState(false);
	const [serverAccessKeyVisible, setServerAccessKeyVisible] = useState(false);
	const [projectInfo, setProjectInfo] = useState<Project>();
	const history = useHistory();
	const userData = useSelector(userSelector);
	const [projectStatusDetails, setProjectStatusDetails] =
		useState<ProjectStatusDto>();
	const [isReadyForSubmission, setIsReadyForSubmission] = useState(false);
	const [isSubmitted, setIsSubmitted] = useState(false);
	const [isReleased, setIsReleased] = useState(false);
	const [updateNameModalVisible, setUpdateNameModalVisible] = useState(false);
	const updateNameModalOpen = useCallback(() => {
		setUpdateNameModalVisible(true);
	}, [updateNameModalVisible]);
	const projectPermissions = userData?.projectPermissions?.find(
		(x) => x.projectId === projectId,
	)?.permissions;

	const loadProjectStatusDetails = async () => {
		return new Promise<void>(async (reslove, reject) => {
			const result = await dispatch(getProjectStatus(projectId));
			if (getProjectStatus.fulfilled.match(result)) {
				reslove();
				setProjectStatusDetails(result.payload);
				setIsReadyForSubmission(getIsReadyForSubmission(result.payload));
				setIsSubmitted(getIsSubmitted(result.payload));
				setIsReleased(getIsReleased(result.payload));
			} else {
				reject();
			}
		});
	};

	const getIsReadyForSubmission = (statusDetails: ProjectStatusDto) =>
		!!statusDetails.hasRequiredDataToSubmit &&
		!isNil(statusDetails.status) &&
		[ProductStatus.Draft, ProductStatus.AwaitingChanges].includes(
			statusDetails.status,
		);

	const getIsSubmitted = (statusDetails: ProjectStatusDto) =>
		!isNil(statusDetails.status) &&
		![ProductStatus.Draft, ProductStatus.AwaitingChanges].includes(
			statusDetails.status,
		);

	const getHasBeenApproved = (statusDetails?: ProjectStatusDto) =>
		!!statusDetails?.hasRequiredDataToSubmit &&
		!isNil(statusDetails.status) &&
		[ProductStatus.ApprovedForStore, ProductStatus.Released].includes(
			statusDetails?.status,
		);

	const getIsReadyToRelease = (statusDetails?: ProjectStatusDto) =>
		!!statusDetails?.hasRequiredDataToSubmit &&
		statusDetails?.status === ProductStatus.ApprovedForStore;

	const getIsReleased = (statusDetails?: ProjectStatusDto) =>
		statusDetails?.status === ProductStatus.Released;

	const ReleaseButton = (statusDetails?: ProjectStatusDto) => {
		if (getIsReleased(statusDetails)) {
			return (
				<Button className={styles.approved}>
					{t('projectSummaryPage.ProgressCard.Released')}
				</Button>
			);
		}
		if (getIsReadyToRelease(statusDetails)) {
			return (
				<Button
					className={styles.released}
					type="primary"
					onClick={showReleaseConfirmation}
				>
					{t('projectSummaryPage.ProgressCard.publishToStore')}
				</Button>
			);
		}
		return '';
	};

	const handleSubmitProjectForReview = () => {
		return dispatch(submitProjectForReview(projectId)).then((result) => {
			if (!submitProjectForReview.fulfilled.match(result)) {
				OmniNotification.error({
					message: t('notification.error.title'),
					description:
						mapErrorToErrorMessage(result.payload) || t('api.defaultError'),
				});
				return;
			}
			OmniNotification.success({
				message: t('projectSummaryPage.Submitted.Message'),
				description: t('projectSummaryPage.Submitted.Description', {
					projectName: projectInfo?.name,
				}),
			});
			history.push('/project/' + projectId);
		});
	};

	const confirmSubmissionTerms = () => {
		const modal = Modal.confirm({
			icon: <InfoCircleOutlined style={{ color: 'red' }} />,
			title: (
				<span className={styles.warningModalHeader}>
					{t('projectSummaryPage.ProgressCard.submitProject.button.0')}
				</span>
			),
			okText: t(
				'projectSummaryPage.ProgressCard.submitProject.confirmation.okText',
			),
			cancelText: t('common.cancel'),
			width: 520,
			okButtonProps: { disabled: true },
			onOk() {
				return handleSubmitProjectForReview();
			},
			cancelButtonProps: {
				className: 'color-dark',
			},
			content: (
				<div className={styles.confirmationForSubmisstionTerms}>
					<p className={styles.mt16}>
						{t(
							'projectSummaryPage.ProgressCard.submitProject.confirmation.contentDescription',
						)}
					</p>
					<p className={styles.mt16}>
						<Checkbox
							onChange={(e) => {
								modal.update((prevConfig) => ({
									...prevConfig,
									okButtonProps: { disabled: !e.target.checked },
								}));
							}}
							data-testid="checkbox"
						/>
						<span style={{ marginLeft: '16px' }}>
							{t(
								'projectSummaryPage.ProgressCard.submitProject.confirmation.checkboxDescription',
							)}
						</span>
						<a
							target="_blank"
							rel="noreferrer"
							href="https://virtuix.com/content-submission-terms"
							style={{ color: '#62A329' }}
						>
							{t(
								'projectSummaryPage.ProgressCard.submitProject.confirmation.checkboxLink',
							)}
							<img
								style={{
									height: '16px',
									width: '16px',
									marginLeft: '4px',
									verticalAlign: 'text-bottom',
								}}
								src={linkIcon}
								alt="logo"
							/>
						</a>
					</p>
					<p style={{ marginBottom: 0 }}>
						{t(
							'projectSummaryPage.ProgressCard.submitProject.confirmation.contentEndline',
						)}
					</p>
				</div>
			),
			centered: true,
		});
	};

	const showReleaseConfirmation = () => {
		Modal.confirm({
			icon: <InfoCircleOutlined style={{ color: 'red' }} />,
			title: (
				<span className={styles.warningModalHeader}>
					{t('projectSummaryPage.Release.Confirmation.title')}
				</span>
			),
			okText: t('projectSummaryPage.Release.Confirmation.OK'),
			cancelText: t('common.cancel'),
			onOk() {
				return handleRelease();
			},
			cancelButtonProps: {
				className: 'color-dark',
			},
			content: (
				<span>{t('projectSummaryPage.Release.Confirmation.content')}</span>
			),
		});
	};

	const handleRelease = () => {
		dispatch(releaseProjectOnStore(projectId)).then((result) => {
			if (!releaseProjectOnStore.fulfilled.match(result)) {
				OmniNotification.error({
					message: t('notification.error.title'),
					description: result.error.message || t('api.defaultError'),
				});
				return;
			}
			OmniNotification.success({
				message: t('projectSummaryPage.Release.Message'),
				description: t('projectSummaryPage.Release.Description', {
					projectName: projectInfo?.name,
				}),
			});
			history.push('/project/' + projectId);
		});
	};

	const loadProjectInfo = async () => {
		return new Promise<void>(async (resolve, reject) => {
			const result = await dispatch(getProject(projectId));
			if (!getProject.fulfilled.match(result)) {
				OmniNotification.error({
					message: t('notification.error.title'),
					description: result.error.message || t('api.defaultError'),
				});
				reject();
				return;
			}
			resolve();
			setProjectInfo(result.payload);
		});
	};
	useEffect(() => {
		setIsLoading(true);
		Promise.all([loadProjectInfo(), loadProjectStatusDetails()]).finally(() => {
			setIsLoading(false);
		});
	}, [dispatch]);

	const getServerAccessToken = () => {
		api.getServerAccessToken().then((res) => {
			copyText(res);
		});
	};

	const copyText = (text: string) => {
		message.success('Copied to clipboard!');
		copy(text);
	};

	if (isLoading) return <Spin />;

	if (!projectInfo) return null;

	const { id, name = '', sdkKey, status, serverKey } = projectInfo;

	function handleCardClick(path: string) {
		history.push(`${id}/${path}`);
	}
	const createAccessKeyForProject = () => {
		if (projectInfo.serverKey) {
			setServerAccessKeyVisible(true);
			return;
		}
		api.generateServerKey(projectId).then((result) => {
			loadProjectInfo().then(() => {
				setServerAccessKeyVisible(true);
			});
		});
	};

	const hasAccessTo = (permission: CollaboratorPermission) => {
		return projectPermissions?.includes(permission);
	};

	const TimeLineDot = ({
		completed,
		reject,
	}: {
		completed?: boolean;
		reject?: boolean;
	}) =>
		completed ? (
			<Button
				className={styles.nodeIcon}
				type="primary"
				shape="circle"
				icon={<CheckOutlined />}
			/>
		) : reject ? (
			<Button
				className={cx(styles.nodeIcon, styles.reject)}
				type="primary"
				shape="circle"
				icon={<CloseOutlined />}
			></Button>
		) : (
			<Button
				className={cx(styles.nodeIcon, styles.pending)}
				disabled={true}
				type="primary"
				shape="circle"
			>
				{' '}
			</Button>
		);

	return (
		<div className="page-wrapper scroll-box">
			<div>
				<Row gutter={[16, 8]}>
					<Col span={24} className={styles.pageHeader}>
						<div className="page-head" style={{ marginBottom: '0px' }}>
							<div>
								<h2 data-testid="projectName">{name}</h2>
							</div>
							<div>
								<Button
									size="large"
									className="ant-btn color-dark"
									onClick={() => updateNameModalOpen()}
									data-testid="updateNameBtn"
								>
									{t('projectSummaryPage.UpdateProjectName')}
								</Button>
							</div>
						</div>
					</Col>
					<Col span={24} className={styles.pageSdkKey}>
						<span data-testid="sdkKey">
							{t('projectSummaryPage.sdkKey', { sdkKey })}
						</span>
						<CopyOutlined
							style={{ color: '#62A329' }}
							className={styles.copyIcon}
							onClick={() => {
								copyText(sdkKey!);
							}}
						/>
					</Col>
				</Row>
				<Row>
					<Col span={24} className={styles.pageSdkKey}>
						<span data-testid="productId">
							{t('projectSummaryPage.productId')} {projectInfo.productId}
						</span>
						<CopyOutlined
							style={{ color: '#62A329' }}
							className={styles.copyIcon}
							onClick={() => {
								copyText(projectInfo.productId!);
							}}
						/>
					</Col>
				</Row>
				<Row>
					<Col span={24} className={styles.pageSdkKey}>
						<span data-testid="userGameToken">
							{t('projectSummaryPage.userGameToken')}
							<span
								onClick={getServerAccessToken}
								className={styles.revealSecretLink}
							>
								{t('common.Get')}
							</span>
						</span>
					</Col>
				</Row>
				<Row gutter={[16, 8]}>
					<Col span={24} className={styles.pageSdkKey + ' ' + styles.serverKey}>
						<span data-testid="serverAccessKey">
							{t('projectSummaryPage.serverAccessKey')}
							<span
								onClick={createAccessKeyForProject}
								data-testid="revealSecretLink"
								className={
									!serverAccessKeyVisible ? styles.revealSecretLink : ''
								}
							>
								{!serverAccessKeyVisible && t('common.Reveal')}
								{serverAccessKeyVisible && projectInfo.serverKey}
							</span>
						</span>
					</Col>
				</Row>

				<Row gutter={16}>
					<Col span={16}>
						<Row gutter={[16, 16]}>
							{hasAccessTo(CollaboratorPermission.Collaborators) && (
								<Col span={12}>
									<Card onClick={() => handleCardClick('collaborators')}>
										<UsersOutlined />
										<h3>{t('projectSummaryPage.Collaborators')}</h3>
										<span>{t('projectSummaryPage.Collaborators.subText')}</span>
									</Card>
								</Col>
							)}
							{hasAccessTo(CollaboratorPermission.StoreDetails) && (
								<Col span={12}>
									<Card onClick={() => handleCardClick('store-details')}>
										<ShopFilled style={{ fontSize: '26px' }} />
										<h3>{t('projectSummaryPage.StoreDetails')}</h3>
										<span>{t('projectSummaryPage.StoreDetails.subText')}</span>
									</Card>
								</Col>
							)}
							{hasAccessTo(CollaboratorPermission.Builds) && (
								<Col span={12}>
									<Card onClick={() => handleCardClick('builds')}>
										<FileZipOutlined style={{ fontSize: '26px' }} />
										<h3>{t('projectSummaryPage.Builds')}</h3>
										<span>{t('projectSummaryPage.Builds.subText')}</span>
									</Card>
								</Col>
							)}
							{hasAccessTo(CollaboratorPermission.GameServices) && (
								<Col span={12}>
									<Card onClick={() => handleCardClick('game-services')}>
										<AppstoreOutlined style={{ fontSize: '26px' }} />
										<h3>{t('projectSummaryPage.GameServices')}</h3>
										<span>{t('projectSummaryPage.GameServices.subText')}</span>
									</Card>
								</Col>
							)}
							{hasAccessTo(CollaboratorPermission.Finances) && (
								<Col span={12}>
									<Card onClick={() => handleCardClick('finance')}>
										<WalletOutlined style={{ fontSize: '26px' }} />
										<h3>{t('projectSummaryPage.Finance')}</h3>
										<span>{t('projectSummaryPage.Finance.subText')}</span>
									</Card>
								</Col>
							)}
							{hasAccessTo(CollaboratorPermission.Players) && (
								<Col span={12}>
									<Card onClick={() => handleCardClick('players')}>
										<SolutionOutlined style={{ fontSize: '26px' }} />
										<h3>{t('projectSummaryPage.Players')}</h3>
										<span>{t('projectSummaryPage.Players.subText')}</span>
									</Card>
								</Col>
							)}
							{hasAccessTo(CollaboratorPermission.Analytics) && (
								<Col span={12}>
									<Card onClick={() => handleCardClick('analytics')}>
										<DotChartOutlined style={{ fontSize: '26px' }} />
										<h3>{t('projectSummaryPage.Analytics')}</h3>
										<span>{t('projectSummaryPage.Analytics.subText')}</span>
									</Card>
								</Col>
							)}
							{hasAccessTo(CollaboratorPermission.RedemptionCodes) && (
								<Col span={12}>
									<Card onClick={() => handleCardClick('redemption-codes')}>
										<BarcodeOutlined style={{ fontSize: '26px' }} />
										<h3>{t('projectSummaryPage.RedemptionCodes')}</h3>
										<span>
											{t('projectSummaryPage.RedemptionCodes.subText')}
										</span>
									</Card>
								</Col>
							)}
						</Row>
					</Col>
					<Col span={8}>
						<Card className={cx('progress-card', styles.progressCard)}>
							<RocketFilled style={{ fontSize: '26px' }} />
							<span>{t('projectSummaryPage.ProgressCard.header')}</span>
							<div className={styles.progressBar}>
								<Timeline>
									<Timeline.Item
										className={styles.timeLineItem}
										dot={
											<TimeLineDot
												completed={
													projectStatusDetails?.hasDetails || isReleased
												}
											/>
										}
									>
										<div className={styles.progressEntry}>
											{hasAccessTo(CollaboratorPermission.StoreDetails) ? (
												<Link to={`${id}/store-details?tab=1`}>
													{t('projectSummaryPage.ProgressCard.storeDetails')}
												</Link>
											) : (
												<span>
													{t('projectSummaryPage.ProgressCard.storeDetails')}
												</span>
											)}
										</div>
									</Timeline.Item>
									<Timeline.Item
										className={styles.timeLineItem}
										dot={
											<TimeLineDot
												completed={
													projectStatusDetails?.hasPaymentDetails || isReleased
												}
											/>
										}
									>
										<div className={styles.progressEntry}>
											{hasAccessTo(CollaboratorPermission.Finances) ? (
												<Link to={`${id}/finance`}>
													{t('projectSummaryPage.ProgressCard.paymentDetails')}
												</Link>
											) : (
												<span>
													{t('projectSummaryPage.ProgressCard.paymentDetails')}
												</span>
											)}
										</div>
									</Timeline.Item>
									<Timeline.Item
										className={styles.timeLineItem}
										dot={
											<TimeLineDot
												completed={
													projectStatusDetails?.hasCompletedAllRequirements ||
													isReleased
												}
											/>
										}
									>
										<div className={styles.progressEntry}>
											{hasAccessTo(CollaboratorPermission.Builds) ? (
												<Link to={`${id}/publishing-requirements`}>
													{t(
														'projectSummaryPage.ProgressCard.publishingRequirements',
													)}
												</Link>
											) : (
												<span>
													{t(
														'projectSummaryPage.ProgressCard.publishingRequirements',
													)}
												</span>
											)}
										</div>
									</Timeline.Item>
									<Timeline.Item
										className={styles.timeLineItem}
										dot={
											<TimeLineDot
												completed={projectStatusDetails?.hasBuild || isReleased}
											/>
										}
									>
										<div className={styles.progressEntry}>
											{hasAccessTo(CollaboratorPermission.Builds) ? (
												<Link to={`${id}/builds`}>
													{t('projectSummaryPage.ProgressCard.uploadBuild')}
												</Link>
											) : (
												<span>
													{t('projectSummaryPage.ProgressCard.uploadBuild')}
												</span>
											)}
										</div>
										<Divider type="horizontal" />
									</Timeline.Item>
								</Timeline>

								<Timeline>
									{projectStatusDetails?.status === ProductStatus.Rejected ? (
										<Timeline.Item
											className={styles.timeLineItem}
											dot={<TimeLineDot reject={true} />}
										>
											<div className={styles.progressEntry}>
												<span>
													{t('projectSummaryPage.ProgressCard.reject')}
												</span>
											</div>
										</Timeline.Item>
									) : (
										<Timeline>
											<Timeline.Item
												className={styles.timeLineItem}
												dot={
													<TimeLineDot completed={isSubmitted || isReleased} />
												}
											>
												<div className={styles.progressEntry}>
													<div className={styles.submit}>
														<span>
															{t(
																'projectSummaryPage.ProgressCard.submitProject',
															)}
														</span>
														{getHasBeenApproved(projectStatusDetails) ? (
															<Button className={styles.approved}>
																{t('projectSummaryPage.ProgressCard.Approved')}
															</Button>
														) : (
															<Button
																disabled={!isReadyForSubmission}
																onClick={confirmSubmissionTerms}
																type="primary"
															>
																{t(
																	'projectSummaryPage.ProgressCard.submitProject.button' +
																		'.' +
																		(isSubmitted
																			? ProductStatus.SubmittedForReview
																			: projectStatusDetails?.status ||
																				ProductStatus.AwaitingChanges),
																)}
															</Button>
														)}
													</div>
												</div>
											</Timeline.Item>
											<Timeline.Item
												className={styles.timeLineItem}
												dot={<TimeLineDot completed={isReleased} />}
											>
												<div className={styles.progressEntry}>
													<div className={styles.submit}>
														<span>
															{t(
																'projectSummaryPage.ProgressCard.afterApproval',
															)}
														</span>
														{ReleaseButton(projectStatusDetails)}
													</div>
												</div>
											</Timeline.Item>
										</Timeline>
									)}
								</Timeline>
							</div>
						</Card>
					</Col>
				</Row>
			</div>
			<UpdateProjectNameModal
				visible={updateNameModalVisible}
				setVisible={(visible) => setUpdateNameModalVisible(visible)}
				afterSubmit={async (name) => {
					setUpdateNameModalVisible(false);
					if (name) {
						loadProjectInfo();
					}
				}}
				projectName={projectInfo.name}
				projectId={projectId}
			/>
		</div>
	);
}
